import { omit } from 'lodash-es'
import {
  ApiClient,
  type ApiClientOptions,
  type CursorPagedResponse,
} from '@/infra/api-client'
import { toURLSearchParams } from '@/utils/url-util'
import type { WorkspaceDomainResource } from '@/features/iam/api/resources/domain'
import type {
  AcceptInvitationRequest,
  CreateInvitationRequest,
  InvitationResource,
} from '@/features/iam/api/resources/invitations'
import type {
  CreateMemberManuallyRequest,
  CreateMemberRequest,
  GetManyMembersByPublicIdRequest,
  GetManyMembersRequest,
  GetMemberRequest,
  ListMembersRequest,
  MemberResource,
  UpdateMemberRequest,
} from '@/features/iam/api/resources/members'
import type {
  ListRolesRequest,
  RoleResource,
} from '@/features/iam/api/resources/role'
import type {
  CreateSkillRequest,
  ListSkillsRequest,
  SkillResource,
  UpdateSkillRequest,
} from '@/features/iam/api/resources/skill'
import type {
  CreateTeamRequest,
  GetManyTeamsRequest,
  GetTeamRequest,
  ListTeamsRequest,
  ListUserTeamsRequest,
  TeamResource,
  UpdateTeamRequest,
} from '@/features/iam/api/resources/team'
import type {
  CreateTeamMemberRequest,
  GetManyTeamMembersRequest,
  GetTeamMemberRequest,
  ListPendingTeamMembersRequest,
  ListTeamMembersRequest,
  ListUserTeamMembersRequest,
  ListWorkspaceMemberTeamMembersByPublicIdRequest,
  ListWorkspaceMemberTeamMembersRequest,
  PendingTeamMemberResource,
  TeamMemberResource,
  UpdateTeamMemberRequest,
} from '@/features/iam/api/resources/team-member'
import type {
  CreateTeamRoleRequest,
  ListTeamRolesRequest,
  TeamRoleResource,
  UpdateTeamRoleRequest,
} from '@/features/iam/api/resources/team-role'
import type {
  PasswordResetRequestResource,
  UserResource,
} from '@/features/iam/api/resources/users'
import type {
  JoinWorkspaceAsAdminResource,
  ListWorkspacesRequest,
  MembershipResource,
  WorkspaceResource,
} from '@/features/iam/api/resources/workspaces'
import type {
  UpdateUserDataRequest,
  UserDataResource,
} from '@/features/iam/api/resources/user-data'
import type { AuthUserResource } from '@/features/iam/api/resources/auth-user'
import type {
  TermsAcceptanceAcceptResource,
  TermsAcceptanceCheckResource,
  TermsOfUseGetResource,
} from '@/features/iam/api/resources/terms-of-use'
import type { EnrollMFAResponse } from '@/features/iam/api/resources/mfa'
import type {
  ProviderResource,
  UpdateProviderUserDataRequest,
} from '@/features/iam/api/resources/provider'
import type {
  GetSearchableUserRequest,
  ListSearchableUsersRequest,
  ListSearchableUsersResponse,
  SearchableUserResource,
} from '@/features/iam/api/resources/searchable-user'
import type {
  GenerateMagicLinkRequest,
  MagicLinkResource,
} from '@/features/iam/api/resources/magic-link'
import type {
  CreateCustomerRequest,
  CreateCustomerResponseResource,
} from '@/features/iam/api/resources/customer'
import type { ClientAssignmentResource } from '@/features/iam/api/resources/client-assignment'

/**
 * Collection of APIs for IAM.
 */
export class IamApi {
  readonly authentication
  readonly clientAssignments
  readonly customers
  readonly domains
  readonly impersonation
  readonly invitations
  readonly magicLinks
  readonly members
  readonly platformAdmin
  readonly providers
  readonly roles
  readonly skills
  readonly teamMembers
  readonly teamRoles
  readonly teams
  readonly termsOfUse
  readonly userData
  readonly userSearch
  readonly users
  readonly workspaces

  constructor(options: ApiClientOptions) {
    this.authentication = new AuthenticateApi(options)
    this.clientAssignments = new ClientAssignmentApi(options)
    this.customers = new CustomerApi(options)
    this.domains = new DomainApi(options)
    this.impersonation = new ImpersonationApi(options)
    this.invitations = new InvitationApi(options)
    this.magicLinks = new MagicLinkApi(options)
    this.members = new MemberApi(options)
    this.platformAdmin = new PlatformAdminApi(options)
    this.providers = new ProviderApi(options)
    this.roles = new RoleApi(options)
    this.skills = new SkillApi(options)
    this.teamMembers = new TeamMemberApi(options)
    this.teamRoles = new TeamRoleApi(options)
    this.teams = new TeamApi(options)
    this.termsOfUse = new TermsOfUseApi(options)
    this.userData = new UserDataApi(options)
    this.userSearch = new UserSearchApi(options)
    this.users = new UserApi(options)
    this.workspaces = new WorkspaceApi(options)
  }
}

export class ClientAssignmentApi extends ApiClient {
  get(
    workspaceId: string,
    clientUserPublicId: string,
    signal?: AbortSignal,
  ): Promise<ClientAssignmentResource> {
    return this._get(
      `v3/workspaces/${workspaceId}/client-assignments/${clientUserPublicId}`,
      undefined,
      { signal },
    )
  }
}

export class CustomerApi extends ApiClient {
  create(
    request: CreateCustomerRequest,
  ): Promise<CreateCustomerResponseResource> {
    return this._post(
      `v3/workspaces/${request.workspace_id}/customers`,
      request,
    )
  }
}

export class DomainApi extends ApiClient {
  get(idOrDomain: string): Promise<WorkspaceDomainResource> {
    return this._get(`v3/domains/${idOrDomain}`)
  }
}

export class InvitationApi extends ApiClient {
  get(id: string, signal?: AbortSignal): Promise<InvitationResource> {
    return this._get(`v3/invitations/${id}`, undefined, { signal })
  }

  create(request: CreateInvitationRequest): Promise<InvitationResource> {
    return this._post('v3/invitations', request)
  }

  accept(
    token: string,
    request: AcceptInvitationRequest,
  ): Promise<MemberResource> {
    return this._post(`v3/invitations/${token}`, request)
  }
}

export class MemberApi extends ApiClient {
  list(
    workspaceId: string,
    request: ListMembersRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<MemberResource>> {
    return this._get(
      `v3/workspaces/${workspaceId}/members`,
      toURLSearchParams({ ...request }, { arrayStyle: 'multi-key-bracket' }),
      { signal },
    )
  }

  /**
   * Member search. `has_permission` matches the platform role merged with the workspace role.
   */
  search(
    workspaceId: string,
    request: ListMembersRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<MemberResource>> {
    return this._get(
      `v3/workspaces/${workspaceId}/members/search`,
      toURLSearchParams({ ...request }, { arrayStyle: 'multi-key-bracket' }),
      { signal },
    )
  }

  get(request: GetMemberRequest): Promise<MemberResource> {
    return this._get(
      `v3/workspaces/${request.workspace_id}/members/${request.user_id}`,
      toURLSearchParams({ ...request }),
    )
  }

  addRole(
    workspaceId: string,
    userPublicId: string,
    roleId: string,
  ): Promise<MemberResource> {
    return this._post(
      `v3.1/workspaces/${workspaceId}/members/${userPublicId}/roles`,
      { role_id: roleId, user_id: userPublicId },
    )
  }

  removeRole(
    workspaceId: string,
    userPublicId: string,
    roleId: string,
  ): Promise<MemberResource> {
    return this._delete(
      `v3.1/workspaces/${workspaceId}/members/${userPublicId}/roles/${roleId}`,
    )
  }

  addSkill(
    workspaceId: string,
    userPublicId: string,
    skillId: string,
  ): Promise<MemberResource> {
    return this._post(
      `v3.1/workspaces/${workspaceId}/members/${userPublicId}/skills`,
      { skill_id: skillId, user_id: userPublicId },
    )
  }

  removeSkill(
    workspaceId: string,
    userPublicId: string,
    skillId: string,
  ): Promise<MemberResource> {
    return this._delete(
      `v3.1/workspaces/${workspaceId}/members/${userPublicId}/skills/${skillId}`,
    )
  }

  addSkills(
    workspaceId: string,
    userPublicId: string,
    skillIds: string[],
  ): Promise<MemberResource> {
    return this._post(
      `v3.1/workspaces/${workspaceId}/members/${userPublicId}/skills/batch`,
      { skill_ids: skillIds },
    )
  }

  removeSkills(
    workspaceId: string,
    userPublicId: string,
    skillIds: string[],
  ): Promise<MemberResource> {
    return this._post(
      `v3.1/workspaces/${workspaceId}/members/${userPublicId}/skills/batch/delete`,
      { skill_ids: skillIds },
    )
  }

  getMany(request: GetManyMembersRequest): Promise<(MemberResource | null)[]> {
    return this._post(
      'v3/members/id-query',
      request.map((param) =>
        param.user_public_id
          ? omit(param, 'user_id')
          : omit(param, 'user_public_id'),
      ),
    )
  }

  getManyByPublicId(
    request: GetManyMembersByPublicIdRequest,
  ): Promise<(MemberResource | null)[]> {
    return this._post('v3/members/id-query', request)
  }

  create(request: CreateMemberRequest): Promise<MemberResource> {
    return this._post('v3/members', request)
  }

  createManually(
    request: CreateMemberManuallyRequest,
  ): Promise<MemberResource> {
    return this._post('v3/members/create-manually', request)
  }

  update(
    workspaceId: string,
    userPublicId: string,
    request: UpdateMemberRequest,
  ): Promise<MemberResource> {
    return this._patch(
      `v3.1/workspaces/${workspaceId}/members/${userPublicId}`,
      request,
    )
  }

  updateLastLogin(workspaceId: string): Promise<void> {
    return this._patch(`v3/workspaces/${workspaceId}/login`, undefined)
  }

  updateLastSeen(workspaceId: string): Promise<void> {
    return this._patch(`v3/workspaces/${workspaceId}/ping`, undefined)
  }

  changeAvatar(
    workspaceId: string,
    userPublicId: string,
    file: File,
  ): Promise<MemberResource | null> {
    const form = new FormData()
    form.append('file', file, file.name)
    return this._put(
      `v3.1/workspaces/${workspaceId}/members/${userPublicId}/avatar`,
      undefined,
      undefined,
      { body: form, headers: {} },
    )
  }
}

export class TeamApi extends ApiClient {
  list(
    request: ListTeamsRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<TeamResource>> {
    return this._get(
      `v3/workspaces/${request.workspace_id}/teams`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  get(request: GetTeamRequest): Promise<TeamResource> {
    if (request.workspace_id) {
      return this._get(
        `v3/workspaces/${request.workspace_id}/teams/${request.id}`,
        toURLSearchParams({ ...request }),
      )
    }
    return this._get(
      `v3/teams/${request.id}`,
      toURLSearchParams({ ...request }),
    )
  }

  getMany(request: GetManyTeamsRequest): Promise<(TeamResource | null)[]> {
    return this._post('v3/teams/id-query', request)
  }

  create(request: CreateTeamRequest): Promise<TeamResource> {
    return this._post('v3/teams', request)
  }

  update(teamId: string, request: UpdateTeamRequest): Promise<TeamResource> {
    return this._patch(`v3/teams/${teamId}`, request)
  }

  changeAvatar(teamId: string, file: File): Promise<TeamResource | null> {
    const form = new FormData()
    form.append('file', file, file.name)
    return this._put(`v3/teams/${teamId}/avatar`, undefined, undefined, {
      body: form,
      headers: {},
    })
  }
}

export class TeamMemberApi extends ApiClient {
  list(
    request: ListTeamMembersRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<TeamMemberResource>> {
    return this._get(
      `v3/teams/${request.team_id}/team-members`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  get(request: GetTeamMemberRequest): Promise<TeamMemberResource> {
    return this._get(
      `v3/teams/${request.team_id}/team-members/${request.user_id}`,
      toURLSearchParams({ ...request }),
    )
  }

  getMany(
    request: GetManyTeamMembersRequest,
  ): Promise<(TeamMemberResource | null)[]> {
    return this._post('v3.1/team-members/id-query', request)
  }

  listForWorkspaceMember(
    request: ListWorkspaceMemberTeamMembersRequest,
    signal?: AbortSignal,
  ): Promise<TeamMemberResource[]> {
    return this._get(
      `v3.1/workspaces/${request.workspace_id}/members/${request.user_id}/team-members`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  listForWorkspaceMemberByPublicId(
    request: ListWorkspaceMemberTeamMembersByPublicIdRequest,
    signal?: AbortSignal,
  ): Promise<TeamMemberResource[]> {
    return this._get(
      `v3.1/workspaces/${request.workspace_id}/members/${request.user_public_id}/team-members`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  listForUser(
    request: ListUserTeamMembersRequest,
    signal?: AbortSignal,
  ): Promise<TeamMemberResource[]> {
    return this._get(
      `v3.1/team-members/${request.user_id}`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  listPending(
    request: ListPendingTeamMembersRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<PendingTeamMemberResource>> {
    return this._get(
      `v3/invitationsPending/`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  create(request: CreateTeamMemberRequest): Promise<TeamMemberResource> {
    return this._post('v3/team-members', request)
  }

  createByPublicId(
    request: CreateTeamMemberRequest,
  ): Promise<TeamMemberResource> {
    return this._post('v3.1/team-members', request)
  }

  update(
    teamId: string,
    userId: string,
    request: UpdateTeamMemberRequest,
  ): Promise<TeamMemberResource> {
    return this._patch(`v3.1/teams/${teamId}/team-members/${userId}`, request)
  }

  delete(request: GetTeamMemberRequest): Promise<void> {
    return this._delete(
      `v3.1/teams/${request.team_id}/team-members/${request.user_id}`,
      undefined,
      toURLSearchParams({ ...request }),
    )
  }
}

export class TeamRoleApi extends ApiClient {
  list(
    request: ListTeamRolesRequest,
    signal?: AbortSignal,
  ): Promise<TeamRoleResource[]> {
    return this._get('v3/team-roles', toURLSearchParams({ ...request }), {
      signal,
    })
  }

  get(id: string): Promise<TeamRoleResource> {
    return this._get(`v3/team-roles/${id}`)
  }

  create(request: CreateTeamRoleRequest): Promise<TeamRoleResource> {
    return this._post('v3/team-roles', request)
  }

  update(
    id: string,
    request: UpdateTeamRoleRequest,
  ): Promise<TeamRoleResource> {
    return this._post(`v3/team-roles/${id}`, request)
  }

  delete(id: string): Promise<void> {
    return this._delete(`v3/team-roles/${id}`)
  }
}

export class SkillApi extends ApiClient {
  get(id: string): Promise<SkillResource> {
    return this._get(`v3/skills/${id}`)
  }

  list(
    request: ListSkillsRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<SkillResource>> {
    return this._get(
      `v3/workspaces/${request.workspace_id}/skills`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  create(request: CreateSkillRequest): Promise<SkillResource> {
    return this._post('v3/skills', request)
  }

  update(id: string, request: UpdateSkillRequest): Promise<SkillResource> {
    return this._post(`v3/skills/${id}`, request)
  }

  delete(id: string): Promise<void> {
    return this._delete(`v3/skills/${id}`)
  }
}

export class RoleApi extends ApiClient {
  list(
    request: ListRolesRequest,
    signal?: AbortSignal,
  ): Promise<RoleResource[]> {
    return this._get(
      `v3/workspaces/${request.workspace_id}/roles`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }
}

export class AuthenticateApi extends ApiClient {
  authenticate(token: string, signal?: AbortSignal): Promise<AuthUserResource> {
    return this._post('v3/auth', {}, undefined, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal,
    })
  }

  enrollMFA(): Promise<EnrollMFAResponse> {
    return this._post('v3/auth/enroll-mfa', undefined)
  }
}

export class UserApi extends ApiClient {
  get(
    publicId: string,
    bearerToken?: string,
    signal?: AbortSignal,
  ): Promise<UserResource> {
    return this._get(`v3.1/users/${publicId}`, undefined, {
      ...(bearerToken && {
        headers: { Authorization: `Bearer ${bearerToken}` },
      }),
      signal,
    })
  }

  getMany(ids: readonly string[]): Promise<(UserResource | null)[]> {
    return this._post(`v3/users/id-query`, ids)
  }

  getManyByPublicId(ids: readonly string[]): Promise<(UserResource | null)[]> {
    return this._post(`v3.1/users/id-query`, ids)
  }

  getUserWorkspaceMemberships(
    userId: string,
    request?: { inactive?: boolean },
    signal?: AbortSignal,
  ): Promise<MembershipResource[]> {
    return this._get(
      `v3.1/users/${userId}/workspace-memberships`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  listTeams(
    request: ListUserTeamsRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<TeamResource>> {
    return this._get(
      `v3/users/${request.userPublicId}/teams`,
      toURLSearchParams({ ...request }, { arrayStyle: 'multi-key-bracket' }),
      { signal },
    )
  }

  getPasswordResetRequest(
    id: string,
    signal?: AbortSignal,
  ): Promise<PasswordResetRequestResource> {
    return this._get(
      `v3/users/reset-password-request/${id}`,
      undefined,
      { signal },
      { unauthenticated: true },
    )
  }

  resetPassword(id: string, password: string): Promise<void> {
    return this._patch(
      `v3/users/reset-password-request/${id}`,
      { password },
      undefined,
      undefined,
      { unauthenticated: true },
    )
  }

  logout(): Promise<void> {
    return this._post('v3/users/logout', undefined)
  }
}

export class ProviderApi extends ApiClient {
  get(publicId: string, signal?: AbortSignal): Promise<ProviderResource> {
    return this._get(`v3/providers/${publicId}`, undefined, { signal })
  }

  updateUserData(
    userPublicId: string,
    request: UpdateProviderUserDataRequest,
  ): Promise<void> {
    return this._patch(`v3/providers/${userPublicId}/user-data`, request)
  }
}

export class UserSearchApi extends ApiClient {
  get(
    publicId: string,
    request: GetSearchableUserRequest,
    signal?: AbortSignal,
  ): Promise<SearchableUserResource> {
    return this._post(`v3/user-search/${publicId}`, request, undefined, {
      signal,
    })
  }

  list(
    request: ListSearchableUsersRequest,
    signal?: AbortSignal,
  ): Promise<ListSearchableUsersResponse> {
    return this._get(
      `v3/user-search/search`,
      toURLSearchParams({ ...request }, { arrayStyle: 'multi-key-bracket' }),
      { signal },
    )
  }
}

export class WorkspaceApi extends ApiClient {
  get(id: string, signal?: AbortSignal): Promise<WorkspaceResource> {
    return this._get(`v3/workspaces/${id}`, undefined, { signal })
  }

  getMany(ids: readonly string[]): Promise<(WorkspaceResource | null)[]> {
    return this._post(
      'v3/workspaces/id-query',
      ids.map((id) => ({ id })),
    )
  }

  list(
    request: ListWorkspacesRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<WorkspaceResource>> {
    return this._get('v3/workspaces', toURLSearchParams({ ...request }), {
      signal,
    })
  }
}

export class UserDataApi extends ApiClient {
  get(userPublicId: string, signal?: AbortSignal): Promise<UserDataResource> {
    return this._get(`v3/users/${userPublicId}/user-data`, undefined, {
      signal,
    })
  }

  async update(
    userPublicId: string,
    request: UpdateUserDataRequest,
  ): Promise<void> {
    await this._put(`v3/users/${userPublicId}/user-data`, request)
  }
}

export class TermsOfUseApi extends ApiClient {
  getLatestForWorkspace(
    workspaceId: string,
    signal?: AbortSignal,
  ): Promise<TermsOfUseGetResource> {
    return this._get(`v3/terms-of-use/${workspaceId}`, undefined, { signal })
  }

  checkAcceptanceForWorkspace(
    workspaceId: string,
    signal?: AbortSignal,
  ): Promise<TermsAcceptanceCheckResource> {
    return this._post(
      'v3/terms-acceptance',
      { workspace_id: workspaceId },
      undefined,
      { signal },
    )
  }

  acceptLatestForWorkspace(
    workspaceId: string,
  ): Promise<TermsAcceptanceAcceptResource> {
    return this._post('v3/terms-acceptance/accept', {
      workspace_id: workspaceId,
    })
  }

  getLatestForPlatform(signal?: AbortSignal): Promise<TermsOfUseGetResource> {
    return this._get('v3/terms-of-use', undefined, { signal })
  }

  checkAcceptanceForPlatform(
    signal?: AbortSignal,
  ): Promise<TermsAcceptanceCheckResource> {
    return this._post('v3/terms-acceptance', undefined, undefined, { signal })
  }

  acceptLatestForPlatform(): Promise<TermsAcceptanceAcceptResource> {
    return this._post('v3/terms-acceptance/accept', undefined)
  }
}

export class ImpersonationApi extends ApiClient {
  impersonate(
    userPublicId: string,
    workspaceId: string,
    callback: string,
  ): Promise<{ url: string }> {
    return this._post('v3/impersonation', {
      user_id: userPublicId,
      workspace_id: workspaceId,
      callback,
    })
  }
}

export class MagicLinkApi extends ApiClient {
  generate(request: GenerateMagicLinkRequest): Promise<MagicLinkResource> {
    return this._post('v4/magic-link', request)
  }
}

export class PlatformAdminApi extends ApiClient {
  joinWorkspaceAsAdmin(
    workspaceId: string,
  ): Promise<JoinWorkspaceAsAdminResource> {
    return this._post(
      `v3/platform-admin/workspaces/${workspaceId}/join`,
      undefined,
    )
  }

  promoteToOwner(workspaceId: string): Promise<MemberResource> {
    return this._post(
      `v3/platform-admin/workspaces/${workspaceId}/promote-to-owner`,
      undefined,
    )
  }
}
